/**
 * Which variant of the app prompt this visitor gets — Amplitude Experiment,
 * remote evaluation, the studio's first web A/B test (Notion task
 * "окно «скачайте приложение» на входе", 2026-11-03).
 *
 * One REST call (`/v1/vardata`) instead of the 400 KB Experiment SDK, keyed
 * by the client deployment `london-tube-web` (Metro project, id 239079). The
 * project API key is not enough: with it `vardata` answers `{}` for every
 * flag, so each flag must have this deployment attached (checked 2026-11-03).
 * A client deployment key is public by design — it only reads variants.
 * Bucketing is by the Amplitude device id the page already uses — the
 * consented one, or the server's per-day visitor id without consent — so a
 * visitor keeps their variant for the visit the prompt is shown in.
 *
 * Each flag is also the switch: turned off in Amplitude it returns no
 * variant and the prompt does not show, with no release. Fail-safe: a slow,
 * blocked or failed call means no prompt and no exposure, so a broken
 * network never skews the split.
 */
export const PROMPT_FLAGS = Object.freeze({
  /** Phone, route page: `a` (benefits, Google Maps-like) vs `b` (rating + station, Moovit-like). */
  routePhone: 'web-app-prompt-route-phone',
  /** Desktop: `v1` (benefits + QR) vs `v2` (app screenshot + QR). */
  desktop: 'web-app-prompt-desktop',
  /** Phone, no route (home, station): one design; `on` shows it. */
  homePhone: 'web-app-prompt-home-phone',
})

export type PromptFlag = (typeof PROMPT_FLAGS)[keyof typeof PROMPT_FLAGS]

const VARDATA_URL = 'https://api.lab.amplitude.com/v1/vardata'

/** Client deployment `london-tube-web` — attached to every flag in `PROMPT_FLAGS`. */
export const PROMPT_DEPLOYMENT_KEY = 'client-qQ9OLgweTptrfXcNUV6AFiVIT1Z0kzLB'

/** How long the prompt waits for a variant before giving up for this page. */
export const VARIANT_TIMEOUT_MS = 1_500

/**
 * The variant of `flag` for `deviceId`, or `undefined` (flag off, visitor
 * not in the rollout, timeout, network or parse failure). Never throws.
 */
export async function fetchPromptVariant(
  flag: PromptFlag,
  deviceId: string,
  deploymentKey: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = VARIANT_TIMEOUT_MS,
): Promise<string | undefined> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const url = `${VARDATA_URL}?device_id=${encodeURIComponent(deviceId)}&flag_key=${encodeURIComponent(flag)}`
    const response = await fetchImpl(url, {
      headers: { Authorization: `Api-Key ${deploymentKey}` },
      signal: controller.signal,
    })
    if (!response.ok) return undefined
    const body = (await response.json()) as Record<string, { key?: unknown } | undefined>
    const key = body[flag]?.key
    return typeof key === 'string' && key !== 'off' && key !== '' ? key : undefined
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}
